<template>
    <div>
        <div class="tw-grid tw-grid-cols-12 tw-mt-4 tw-mb-4">
            <div class="tw-col-span-6 tw-flex tw-items-center tw-space-x-6">
                <div
                    class="tw-flex-grow-0 clickable"
                    tabindex="0"
                    @click="toggleShowReviews(employee)"
                    @keyup.enter="toggleShowReviews(employee)"
                >
                    <l-profile-picture :user="employee.user" size="xm" />
                </div>

                <div
                    class="tw-flex-1 tw-truncate"
                    @click="toggleShowReviews(employee)"
                    @keyup.enter="toggleShowReviews(employee)"
                >
                    <div class="tw-truncate clickable" tabindex="0">
                        <span class="fake-link">
                            <span class="tw-font-bold tw-mr-2">
                                {{ employee.user.fullname }}
                            </span>
                            <br />
                            <span class="color-primary">
                                {{
                                    $t(`reviews.list.labelShowAssessments`, {
                                        count: employee.instances.length,
                                    })
                                }}
                            </span>
                        </span>
                    </div>
                </div>
            </div>

            <div class="tw-col-span-2">
                <span
                    v-if="employee.latest"
                    v-b-popover.hover.top="{
                        content: $t(`reviews.list.labelDateLatest`),
                    }"
                >
                    {{ employee.latest.date | moment("MMM DD, YYYY") }}
                </span>
            </div>
            <div class="tw-col-span-2">
                <template v-if="employee.latest">
                    <span
                        v-if="employee.latest.score"
                        v-b-popover.hover.top="{
                            content: $t(`reviews.list.labelScore`),
                        }"
                    >
                        <strong>{{ employee.latest.score }}</strong>
                    </span>
                    <span
                        v-else
                        v-b-popover.hover.top="{
                            content: $t(`reviews.list.labelPendingReview`),
                        }"
                        class="color-grey"
                    >
                        <i class="fal fa-clock-o"></i>
                    </span>
                </template>
            </div>
            <div class="tw-col-span-2 tw-pt-2">
                <l-progress :progress-percent="employee.progress" />
            </div>
        </div>

        <div
            v-show="showReviews"
            class="tw-border-0 tw-border-t tw-border-b tw-border-gray-200 tw-border-solid"
        >
            <div v-show="!employee.latest" class="tw-p-2 tw-bg-gray-50 text-center">
                <p>{{ $t(`reviews.list.showReviewsEmpty`) }}</p>
            </div>

            <div v-for="instance in employee.instances" :key="instance._id">
                <div
                    class="tw-grid tw-grid-cols-12 tw-p-4 tw-bg-gray-100 tw-border-0 tw-border-b-2 tw-border-white tw-border-solid"
                >
                    <div class="tw-col-span-6 tw-truncate">
                        <span
                            class="fake-link"
                            :class="{ clickable: rights.R_CREATE }"
                            tabindex="0"
                            @click="
                                reviewCycleUpdateStatus({
                                    reviewCycleId: instance._id,
                                })
                            "
                            @keyup.enter="
                                reviewCycleUpdateStatus({
                                    reviewCycleId: instance._id,
                                })
                            "
                        >
                            <span class="r-default-margin-s">{{ instance.name }}</span>
                        </span>
                    </div>

                    <div class="tw-col-span-2">
                        <span>
                            <span
                                v-b-popover.hover.top="{
                                    content: $t(`reviews.list.labelDatePast`),
                                }"
                            >
                                {{ instance.date | moment("MMM DD, YYYY") }}
                            </span>
                        </span>
                    </div>
                    <div class="tw-col-span-2">
                        <span
                            v-show="instance.score"
                            v-b-popover.hover.top="{
                                content: $t(`reviews.list.labelScore`),
                            }"
                        >
                            <strong>{{ instance.score }}</strong>
                        </span>
                        <span
                            v-show="!instance.score"
                            v-b-popover.hover.top="{
                                content: $t(`reviews.list.labelPendingReview`),
                            }"
                            class="color-grey"
                        >
                            <i class="fal fa-clock-o"></i>
                        </span>
                    </div>
                    <div class="tw-col-span-2">
                        <span class="pull-right">
                            <a
                                class="fake-link clickable"
                                tabindex="0"
                                @click="
                                    reviewCycleUpdate({
                                        reviewCycleId: instance._id,
                                        revieweeId: employee.user._id,
                                        role: 'manager',
                                        view: 'overview',
                                        contributorId: instance.manager && instance.manager._id,
                                        viewAs: 'owner',
                                        entrypoint: 'adminByUser',
                                    })
                                "
                                @keyup.enter="
                                    reviewCycleUpdate({
                                        reviewCycleId: instance._id,
                                        revieweeId: employee.user._id,
                                        role: 'manager',
                                        view: 'overview',
                                        contributorId: instance.manager && instance.manager._id,
                                        viewAs: 'owner',
                                        entrypoint: 'adminByUser',
                                    })
                                "
                            >
                                <span class="tw-font-semibold">{{
                                    $t(`reviews.list.showDetails`)
                                }}</span>
                            </a>
                        </span>
                    </div>
                </div>

                <div
                    v-for="(contributor, index) in instance.contributors"
                    :key="contributor._id"
                    class="tw-grid tw-grid-cols-12 tw-bg-gray-50 tw-p-4 tw-border-0 tw-border-white tw-border-solid"
                    :class="{ 'tw-border-b-2': index !== instance.contributors.length - 1 }"
                >
                    <div class="tw-col-span-6">
                        {{ contributor.anon ? "Anonymous" : contributor.user.fullname }}
                    </div>

                    <div class="tw-col-span-2">
                        <span>
                            {{ getContributorRoleText(contributor.role) }}
                        </span>
                    </div>

                    <div class="tw-col-span-2">
                        <span
                            class="label"
                            :class="{
                                'label-light': contributor.status === 'pending',
                                'label-warning':
                                    contributor.status === 'started' ||
                                    contributor.status === 'declined',
                                'label-royal':
                                    contributor.status === 'complete' ||
                                    contributor.status === 'shared' ||
                                    contributor.status === 'signed',
                            }"
                        >
                            {{ getContributorStatusText(contributor.status) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LProfilePicture from "@/components/LProfilePicture";
import LProgress from "@/components/LProgress";

export default {
    components: {
        LProfilePicture,
        LProgress,
    },
    props: {
        employee: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            showReviews: false,
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
        }),
    },
    methods: {
        toggleShowReviews() {
            this.showReviews = !this.showReviews;
        },

        getContributorStatusText(status) {
            switch (status) {
                case "pending":
                    return this.$t(`reviews.admin.labelStatusNotStarted`);
                case "started":
                    return this.$t(`reviews.admin.labelStatusStarted`);
                case "complete":
                    return this.$t(`reviews.admin.labelStatusComplete`);
                case "shared":
                    return this.$t(`reviews.admin.labelStatusShared`);
                case "signed":
                    return this.$t(`reviews.admin.labelStatusSigned`);
                case "declined":
                    return this.$t(`reviews.admin.labelStatusDeclined`);
                default:
                    return "";
            }
        },

        getContributorRoleText(role) {
            switch (role) {
                case "peer":
                    return this.$t(`reviews.admin.labelStatusPeerReview`);
                case "directReport":
                    return this.$t(`reviews.admin.labelStatusDirectReportReview`);
                case "manager":
                    return this.$t(`reviews.admin.labelStatusManagerReview`);
                case "additionalManager":
                    return this.$t(`reviews.admin.labelStatusAdditionalManagerReview`);
                case "reviewee":
                    return this.$t(`reviews.admin.labelStatusSelfReview`);
                default:
                    return "";
            }
        },

        reviewCycleUpdate(opts) {
            this.$router.push(
                `/reviews/update/${opts.reviewCycleId}/reviewee/${opts.revieweeId}/${opts.role}/${opts.contributorId}/viewAs/${opts.viewAs}/${opts.view}/${opts.entrypoint}`,
            );
        },
        reviewCycleUpdateStatus(opts) {
            if (this.rights.R_CREATE) {
                this.$router.push(`/reviews/cycles/edit/${opts.reviewCycleId}/status`);
            }
        },
    },
};
</script>
